import {useAllFieldsQuery} from '@transactions/graphql';
import * as React from 'react';
import {CategoryInlineDisplay} from 'src/CategoryInlineDisplay';
import InputGroup from 'src/InputGroup';
import {MultiFieldDisplay} from 'src/inputs/FieldInput';
import MultiTagDisplay from 'src/inputs/TagInput/MultiTagDisplay';
import {formatFullDate} from 'src/lib/displayDate';
import {TRANSFER_TYPE_LABELS} from 'src/TransferKindSelect';
import type {DetailTransactionFragment} from '@transactions/graphql';

// InputGroup labels carry pt-1.5 to line up with the first line of an h-9
// input; plain-text values need the same offset to sit on that line. This
// mirrors the raw transaction Display, so the two detail pages read as one
// pattern rather than two.
const Value = ({children}: {children: React.ReactNode}) => (
  <div className="pt-1.5">{children}</div>
);

// An unset value renders as an em dash rather than an empty row: a blank
// space beside a label is indistinguishable from a rendering bug.
const Empty = () => <span className="text-muted-foreground">—</span>;

const PendingValue = ({pending}: {pending: boolean}) => (
  <Value>{pending ? 'Pending' : 'Posted'}</Value>
);

/**
 * Tags and fields as read-only chips — the display half of TagsAndFieldsInput.
 * Field names live on the Field documents rather than on the transaction, so
 * the field list is fetched (from the cache, in practice: every list view
 * already asks for it).
 */
const TagsAndFieldsValue = ({
  transaction,
}: {
  transaction: DetailTransactionFragment;
}) => {
  const {data} = useAllFieldsQuery();
  const fields = data?.allFields ?? [];
  const tags = transaction.tags.map((tag) => tag.name);
  // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
  const fieldValues = (transaction.fieldValues || {}) as Record<string, string>;
  const hasFieldValues = Object.values(fieldValues).some(
    (value) => value != null && value !== '',
  );

  if (tags.length === 0 && !hasFieldValues) {
    return (
      <Value>
        <Empty />
      </Value>
    );
  }

  return (
    <Value>
      <div className="flex flex-col gap-1.5">
        {tags.length > 0 && <MultiTagDisplay tags={tags} />}
        {hasFieldValues && (
          <MultiFieldDisplay fields={fields} fieldValues={fieldValues} />
        )}
      </div>
    </Value>
  );
};

/** Read-only details of a payment; the Edit button opens the form. */
export const PaymentDisplay = ({
  payment,
}: {
  payment: DetailTransactionFragment;
}) => (
  <div className="flex flex-col gap-2 p-4">
    <InputGroup label="Payee">
      <Value>{payment.payee || <Empty />}</Value>
    </InputGroup>
    <InputGroup label="Note">
      <Value>{payment.description || <Empty />}</Value>
    </InputGroup>
    <InputGroup label="Date">
      <Value>{formatFullDate(payment.date)}</Value>
    </InputGroup>
    <InputGroup label="Category">
      <Value>
        {payment.category ? (
          <CategoryInlineDisplay category={payment.category} />
        ) : (
          <Empty />
        )}
      </Value>
    </InputGroup>
    <InputGroup label="Tags & fields">
      <TagsAndFieldsValue transaction={payment} />
    </InputGroup>
    <InputGroup label="Status">
      <PendingValue pending={payment.pending} />
    </InputGroup>
  </div>
);

/** Read-only details of a transfer; the Edit button opens the form. */
export const TransferDisplay = ({
  transfer,
}: {
  transfer: DetailTransactionFragment;
}) => (
  <div className="flex flex-col gap-2 p-4">
    <InputGroup label="Type">
      <Value>
        {transfer.transferType ? (
          TRANSFER_TYPE_LABELS[transfer.transferType]
        ) : (
          <Empty />
        )}
      </Value>
    </InputGroup>
    <InputGroup label="Payee">
      <Value>{transfer.payee || <Empty />}</Value>
    </InputGroup>
    <InputGroup label="Date">
      <Value>{formatFullDate(transfer.date)}</Value>
    </InputGroup>
    <InputGroup label="Note">
      <Value>{transfer.description || <Empty />}</Value>
    </InputGroup>
    <InputGroup label="Tags & fields">
      <TagsAndFieldsValue transaction={transfer} />
    </InputGroup>
    <InputGroup label="Status">
      <PendingValue pending={transfer.pending} />
    </InputGroup>
  </div>
);
